// Моделът на „Целия списък" (CatalogPickerModal) — чисти функции без React:
// типът на реда, търсенето по няколко думи (име + вторичния текст), броячите
// по група, превключвателите „Само с наличност" / „Под минимума" / „От този
// доставчик" / „Скрий добавените", отметките (вкл. „всички видими" с три
// състояния) и клавишният редуктор. Въвеждането в реда (количество, цена,
// сумата) е в catalogEntryModel.ts. Компонентът само държи състоянието и
// рисува — всичко, което може да се сбърка, е тук и е покрито от
// catalogPickerModel.test.ts.

// Тон на наличността: none — няма (червеникаво), low — малко (оранжево),
// ok — има. Листът решава праговете; тук е само речникът.
export type CatalogStockTone = "none" | "low" | "ok";

export type CatalogItem = {
  id: number;
  name: string;
  group: string | null;
  // Метатекстът на заварения ред („кг · Наличност 12,500 · посл. цена 2.40") —
  // приглушено до името. Ползва се само когато листът НЕ подава колоните.
  meta?: string;
  // Вече е в документа: значка, и по подразбиране не може да се отметне
  // (виж `addedSelectable` на модала).
  added?: boolean;
  // Структурираните колони — вече форматирани от листа (запетая за
  // количества, точка за пари). Подаде ли ги, редът става решетка
  // Продукт | Ед. | Наличност | Цена с лепнат хедър.
  unit?: string;
  stock?: string;
  stockTone?: CatalogStockTone;
  price?: string;
  // Вторичният текст до името, приглушено на СЪЩИЯ ред („−1,000 продадени
  // без наличност", „Рецепта") — търсенето гледа и него.
  sub?: string;
  // Колона „Мин." (минималното количество, форматирано) и чипът „Под
  // минимума" (наличността е под него — удобно за поръчка)
  minimum?: string;
  belowMinimum?: boolean;
  // Чипът „От този доставчик" (доставка с избран доставчик): има цена от него
  fromSupplier?: boolean;
  // ─── Въвеждането в реда (само когато модалът е с `entry`) ───
  // мярката до полето за количество (базовата, разфасовката на реда, „порц.")
  entryUnit?: string;
  // базови единици в една `entryUnit` (разфасовка) — за сумата; без него 1
  entryFactor?: number;
  // предложената ед. цена нето за базова единица (с точка): цената на реда в
  // листа, иначе последната доставна — с нея полето „Цена" е попълнено
  suggestedPrice?: string;
  // Стойността на реда в листа, ако има („5,000"): виждa се приглушено, а
  // въведеното тук я презаписва само след въпрос
  filledValue?: string;
};

// Специалните ключове на групите: „Всички" и „Без група"
export const ALL_GROUPS = "__all__";
export const NO_GROUP = "__none__";

export const groupKeyOf = (item: CatalogItem): string => item.group || NO_GROUP;

export const searchWordsOf = (query: string): string[] =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

// Малките букви се смятат веднъж на каталог, не на всеки клавиш (1–3 хил. реда)
export type IndexedCatalogItem = {
  item: CatalogItem;
  nameLower: string;
  subLower: string;
};

export const indexCatalog = (items: CatalogItem[]): IndexedCatalogItem[] =>
  items.map((item) => ({
    item,
    nameLower: item.name.toLowerCase(),
    subLower: (item.sub ?? "").toLowerCase(),
  }));

// Всяка дума трябва да я има в името ИЛИ във втория ред
export const matchesWords = (entry: IndexedCatalogItem, words: string[]): boolean =>
  words.every((word) => entry.nameLower.includes(word) || entry.subLower.includes(word));

// ─── Филтриране и броячи ────────────────────────────────────────────────────
export type CatalogFilters = {
  words: string[];
  group: string;
  onlyInStock: boolean;
  hideAdded: boolean;
  // чиповете „Под минимума" и „От този доставчик" (липсват — изключени)
  belowMinimum?: boolean;
  fromSupplier?: boolean;
};

export type CatalogView = {
  // редовете на избраната група, в реда на каталога
  visible: CatalogItem[];
  // брой по ключ на група СЛЕД текста и превключвателите (без групата) —
  // това показват релсата и чиповете
  counts: Map<string, number>;
  // колко минават текста и превключвателите във всички групи („Всички")
  total: number;
};

// „Само с наличност" скрива САМО изрично празните (stockTone === "none"):
// ред без тон (напр. рецепта в изписването) няма наличност, която да се
// провери, и остава. „Под минимума" и „От този доставчик" оставят само
// изрично отбелязаните.
const passesToggles = (item: CatalogItem, filters: CatalogFilters) =>
  !(filters.hideAdded && item.added) &&
  !(filters.onlyInStock && item.stockTone === "none") &&
  !(filters.belowMinimum && !item.belowMinimum) &&
  !(filters.fromSupplier && !item.fromSupplier);

export const catalogViewOf = (
  index: IndexedCatalogItem[],
  filters: CatalogFilters,
): CatalogView => {
  const counts = new Map<string, number>();
  const visible: CatalogItem[] = [];
  let total = 0;
  for (const entry of index) {
    const { item } = entry;
    if (!passesToggles(item, filters) || !matchesWords(entry, filters.words)) continue;
    total += 1;
    const key = groupKeyOf(item);
    counts.set(key, (counts.get(key) ?? 0) + 1);
    if (filters.group === ALL_GROUPS || filters.group === key) visible.push(item);
  }
  return { visible, counts, total };
};

// Групите на релсата — от ЦЕЛИЯ каталог (не от филтъра), за да не скачат,
// докато се пише; азбучно, „Без група" накрая (само ако има и истински групи).
export const catalogGroupsOf = (items: CatalogItem[]): string[] => {
  const named = new Set<string>();
  let hasUngrouped = false;
  for (const item of items) {
    if (item.group) named.add(item.group);
    else hasUngrouped = true;
  }
  const groups = Array.from(named).sort((a, b) => a.localeCompare(b, "bg"));
  return groups.length && hasUngrouped ? [...groups, NO_GROUP] : groups;
};

// ─── Колоните ───────────────────────────────────────────────────────────────
export type CatalogColumns = {
  unit: boolean;
  stock: boolean;
  price: boolean;
  minimum: boolean;
  sub: boolean;
  // поне една от колоните → решетка; иначе заварения двуредов ред с `meta`
  structured: boolean;
};

export const catalogColumnsOf = (items: CatalogItem[]): CatalogColumns => {
  const columns = { unit: false, stock: false, price: false, minimum: false, sub: false };
  for (const item of items) {
    if (item.unit) columns.unit = true;
    if (item.stock) columns.stock = true;
    if (item.price) columns.price = true;
    if (item.minimum) columns.minimum = true;
    if (item.sub) columns.sub = true;
  }
  return { ...columns, structured: columns.unit || columns.stock || columns.price };
};

// ─── Отметки ────────────────────────────────────────────────────────────────
export const isSelectable = (item: CatalogItem, addedSelectable: boolean): boolean =>
  addedSelectable || !item.added;

export const togglePicked = (picked: Set<number>, id: number): Set<number> => {
  const next = new Set(picked);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

export type TriState = "none" | "some" | "all";

// Отметката „всички видими" в хедъра: гледа само редовете, които могат да
// се отметнат (вече добавените по подразбиране не броят).
export const visibleSelectionOf = (
  visible: CatalogItem[],
  picked: Set<number>,
  addedSelectable: boolean,
): { state: TriState; selectableCount: number } => {
  let selectableCount = 0;
  let pickedCount = 0;
  for (const item of visible) {
    if (!isSelectable(item, addedSelectable)) continue;
    selectableCount += 1;
    if (picked.has(item.id)) pickedCount += 1;
  }
  const state: TriState =
    pickedCount === 0 ? "none" : pickedCount === selectableCount ? "all" : "some";
  return { state, selectableCount };
};

// Всички видими отметнати → маха ги; иначе отмята всички видими (отметките
// извън филтъра остават непокътнати)
export const toggleAllVisible = (
  picked: Set<number>,
  visible: CatalogItem[],
  addedSelectable: boolean,
): Set<number> => {
  const { state } = visibleSelectionOf(visible, picked, addedSelectable);
  const next = new Set(picked);
  for (const item of visible) {
    if (!isSelectable(item, addedSelectable)) continue;
    if (state === "all") next.delete(item.id);
    else next.add(item.id);
  }
  return next;
};

// Какво добавя Ctrl/Cmd+Enter (и бутонът): отметнатите в реда на каталога (не
// в реда на отмятане); няма ли отметнати — маркирания ред, ако може да се
// избере (бързият път „пиши → ↓ → Ctrl+Enter" за един продукт).
export const idsToAdd = (
  items: CatalogItem[],
  picked: Set<number>,
  active: CatalogItem | undefined,
  addedSelectable: boolean,
): number[] => {
  if (picked.size)
    return items
      .filter((item) => picked.has(item.id) && isSelectable(item, addedSelectable))
      .map((item) => item.id);
  return active && isSelectable(active, addedSelectable) ? [active.id] : [];
};

// При нов филтър маркерът застава на първия ред, който може да се отметне
export const firstSelectableIndex = (visible: CatalogItem[], addedSelectable: boolean): number =>
  Math.max(0, visible.findIndex((item) => isSelectable(item, addedSelectable)));

// ─── Клавиатура (фокусът е във филтъра) ─────────────────────────────────────
export type CatalogKey = {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey?: boolean;
};

export type CatalogKeyContext = {
  activeIndex: number;
  rowCount: number;
  // колко реда се виждат наведнъж (PageUp/PageDown)
  pageSize: number;
  hasQuery: boolean;
  // Enter при празен списък: „Покажи от всички групи" (има съвпадения другаде —
  // пази от дубликат), иначе „+ Нов продукт" (ако листът го поддържа)
  enterOnEmpty: "allGroups" | "create" | null;
  // Модалът е с въвеждане в реда: Enter отмята И отива в полето („edit"),
  // Shift+Enter само отмята/маха отметката
  entryMode?: boolean;
};

export type CatalogKeyAction =
  | { type: "move"; index: number }
  | { type: "group"; step: -1 | 1 }
  | { type: "toggle" }
  | { type: "edit" }
  | { type: "add" }
  | { type: "allGroups" }
  | { type: "create" }
  | { type: "clearQuery" }
  | { type: "close" };

const clampIndex = (index: number, rowCount: number) =>
  Math.max(0, Math.min(index, rowCount - 1));

// null → клавишът не е наш (буквите, Space, ←/→ в текста остават на полето)
export const catalogKeyAction = (
  event: CatalogKey,
  context: CatalogKeyContext,
): CatalogKeyAction | null => {
  const command = event.ctrlKey || event.metaKey;
  const { activeIndex, rowCount, pageSize } = context;
  const move = (index: number): CatalogKeyAction | null =>
    rowCount ? { type: "move", index: clampIndex(index, rowCount) } : null;

  switch (event.key) {
    case "Enter":
      if (command) return { type: "add" };
      if (event.altKey) return null;
      if (rowCount) return context.entryMode && !event.shiftKey ? { type: "edit" } : { type: "toggle" };
      return context.enterOnEmpty ? { type: context.enterOnEmpty } : null;
    case "Escape":
      return context.hasQuery ? { type: "clearQuery" } : { type: "close" };
    case "ArrowDown":
      return event.altKey ? { type: "group", step: 1 } : move(activeIndex + 1);
    case "ArrowUp":
      return event.altKey ? { type: "group", step: -1 } : move(activeIndex - 1);
    case "PageDown":
      return move(activeIndex + pageSize);
    case "PageUp":
      return move(activeIndex - pageSize);
    case "Home":
      return command ? move(0) : null;
    case "End":
      return command ? move(rowCount - 1) : null;
    default:
      return null;
  }
};

// Alt+↑/↓ — следващата/предишната група по кръга (Всички → групите → Всички)
export const stepGroup = (groups: string[], current: string, step: -1 | 1): string => {
  const ring = [ALL_GROUPS, ...groups];
  const position = Math.max(0, ring.indexOf(current));
  return ring[(position + step + ring.length) % ring.length];
};
